import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useRouterState } from "@tanstack/react-router";

interface HeaderProps {
  isScrolled: boolean;
}

export function Header({ isScrolled }: HeaderProps) {
  const { i18n } = useTranslation();
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const currentPath = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangDropdownOpen(false);
  };

  const languages = [
    { code: "en", label: "EN", flag: "🇺🇸", name: "English" },
    { code: "zh-CN", label: "简", flag: "🇨🇳", name: "简体中文" },
    { code: "zh-TW", label: "繁", flag: "🇹🇼", name: "繁體中文" },
    { code: "ja", label: "JA", flag: "🇯🇵", name: "日本語" },
    { code: "ko", label: "KO", flag: "🇰🇷", name: "한국어" },
    { code: "de", label: "DE", flag: "🇩🇪", name: "Deutsch" },
    { code: "ru", label: "RU", flag: "🇷🇺", name: "Русский" },
    { code: "ar", label: "AR", flag: "🇸🇦", name: "العربية" },
  ];

  const currentLang = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const subtitle = i18n.language === "ja"
    ? "オンラインテキストツール"
    : i18n.language === "ko"
    ? "온라인 텍스트 도구"
    : i18n.language === "de"
    ? "Online-Textwerkzeuge"
    : i18n.language === "ru"
    ? "Онлайн-инструменты для текста"
    : i18n.language === "ar"
    ? "أدوات النص عبر الإنترنت"
    : i18n.language === "zh-CN"
    ? "在线文本工具"
    : i18n.language === "zh-TW"
    ? "線上文字工具"
    : "Online Text Tools";

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo */}
        <button
          onClick={() => navigate({ to: "/char-count" })}
          className="flex items-center gap-3 group cursor-pointer bg-transparent border-none p-0"
        >
          <div className="relative w-12 h-12 flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-xl opacity-0 group-hover:opacity-50 blur-xl transition-opacity duration-300" />
            </div>
            <svg
              className="relative z-10 w-7 h-7 text-white group-hover:scale-110 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div className="text-left">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-700 group-hover:to-indigo-700 transition-all duration-300">
              Text Tools
            </h1>
            <p className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors duration-300">
              {subtitle}
            </p>
          </div>
        </button>

        {/* Navigation Links */}
        <nav className="hidden md:flex items-center gap-4">
          <button
            onClick={() => navigate({ to: "/char-count" })}
            className={`text-sm font-medium transition-colors hover:text-blue-600 ${
              currentPath === "/char-count" ? "text-blue-600" : "text-gray-700"
            }`}
          >
            {i18n.language === "ja" ? "文字カウンター" : i18n.language === "ko" ? "문자 카운터" : i18n.language === "de" ? "Zeichenzähler" : i18n.language === "ru" ? "Счётчик символов" : i18n.language === "ar" ? "عداد الأحرف" : i18n.language === "zh-CN" ? "字符统计" : i18n.language === "zh-TW" ? "字元統計" : "Character Counter"}
          </button>
          <button
            onClick={() => navigate({ to: "/text-formatter" })}
            className={`text-sm font-medium transition-colors hover:text-orange-600 ${
              currentPath === "/text-formatter" ? "text-orange-600" : "text-gray-700"
            }`}
          >
            {i18n.language === "ja" ? "テキスト美化" : i18n.language === "ko" ? "텍스트 미화" : i18n.language === "de" ? "Textformatierung" : i18n.language === "ru" ? "Форматирование текста" : i18n.language === "ar" ? "تنسيق النص" : i18n.language === "zh-CN" ? "文本美化" : i18n.language === "zh-TW" ? "文字美化" : "Text Formatter"}
          </button>
          <button
            onClick={() => navigate({ to: "/login" })}
            className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
          >
            {i18n.language === "ja" ? "ログイン" : i18n.language === "ko" ? "로그인" : i18n.language === "de" ? "Anmelden" : i18n.language === "ru" ? "Войти" : i18n.language === "ar" ? "تسجيل الدخول" : i18n.language === "zh-CN" ? "登录" : i18n.language === "zh-TW" ? "登入" : "Login"}
          </button>
        </nav>

        {/* Language Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:shadow-md transition-all duration-200"
          >
            <span className="text-xl">{currentLang.flag}</span>
            <span className="font-medium text-gray-700">{currentLang.label}</span>
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                isLangDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isLangDropdownOpen && (
            <div
              className={`absolute ${
                i18n.language === "ar" ? "left-0" : "right-0"
              } mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-dropdown`}
            >
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-blue-50 transition-colors duration-150 ${
                    i18n.language === lang.code ? "bg-blue-50 text-blue-600" : "text-gray-700"
                  }`}
                >
                  <span className="text-2xl">{lang.flag}</span>
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{lang.label}</span>
                    <span className="text-xs text-gray-500">{lang.name}</span>
                  </div>
                  {i18n.language === lang.code && (
                    <svg className="w-5 h-5 ml-auto text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
